/**
 * Link targets an App view may use. Server-safe: the catalog validates `href`
 * with it, and the renderer re-checks, so a `javascript:` URL can never reach
 * an anchor.
 *
 * Allowed: an app-internal path (`/suppliers/42`), `http(s)://…`, `mailto:…`.
 */
const INTERNAL = /^\/(?![/\\])/;
const EXTERNAL = /^(?:https?:\/\/[^/?#]|mailto:.)/i;
// oxlint-disable-next-line no-control-regex -- rejecting control characters is the point
const FORBIDDEN_CHARS = /[\s\u0000-\u001f\u007f]/;

export function isSafeHref(href: string): boolean {
  if (href.length === 0 || href.length > 2048) return false;
  if (FORBIDDEN_CHARS.test(href)) return false;
  return INTERNAL.test(href) || EXTERNAL.test(href);
}

/** Absolute URLs leave the app; everything else is an in-app path. */
export function isExternalHref(href: string): boolean {
  return !INTERNAL.test(href);
}

const PLACEHOLDER = /\{([^{}]+)\}/g;

/** Whether a column `href` template is a safe link once its `{field}` slots are filled. */
export function isSafeHrefTemplate(template: string): boolean {
  return isSafeHref(template.replace(PLACEHOLDER, "x"));
}

/**
 * Fill a column `href` template (`/invoices/{id}`) from a row. Values are
 * URL-encoded. Returns null when a referenced field is empty, so the cell
 * renders as plain text rather than a broken link.
 */
export function resolveHrefTemplate(
  template: string,
  row: Record<string, unknown>,
): string | null {
  let missing = false;
  const href = template.replace(PLACEHOLDER, (_, key: string) => {
    const value = row[key.trim()];
    if (value === null || value === undefined || value === "") {
      missing = true;
      return "";
    }
    return encodeURIComponent(String(value));
  });
  return missing || !isSafeHref(href) ? null : href;
}

/**
 * Table props with any unsafe column `href` removed, so one bad link degrades
 * that column to plain text instead of failing the whole `columns` array.
 */
export function withSafeColumnHrefs(raw: unknown): unknown {
  if (typeof raw !== "object" || raw === null || !("columns" in raw))
    return raw;
  const { columns } = raw;
  if (!Array.isArray(columns)) return raw;
  const safe: unknown[] = columns.map((column: unknown) => {
    if (typeof column !== "object" || column === null) return column;
    if (!("href" in column) || typeof column.href !== "string") return column;
    if (isSafeHrefTemplate(column.href)) return column;
    const { href: _dropped, ...rest } = column;
    return rest;
  });
  return { ...raw, columns: safe };
}
